import moment from "moment";
import * as XLSXStyle from "xlsx-js-style";
import { descargarLibro, limpiarNombreArchivo } from "../../../../../Components/ExcelTablaReporte";

export const VERDE = "#389E0D";
export const ROJO = "#CF1322";

export const COLORES_BIWO = ["#00BFD6", "#006B7A", "#2EC4B6", "#1A3A4A", "#81D4FA", "#2D929E"];

const r1 = (n) => Math.round(n * 10) / 10;

const tipoObjetivoDe = (etapa) => etapa.tipoObjetivo || (etapa.esFinal ? "minimo" : "maximo");

const etiquetaCategoria = (etapa) => (etapa.esFinal ? "OK" : etapa.esDefectuoso ? "SCRAP" : etapa.esRetrabajo ? "RETRABAJO" : (etapa.nombre || "").trim().toUpperCase());

const categoriasDeGrupo = (grupo) => {
  const conObjetivo = new Set(grupo.ordenes.flatMap((o) => (o.porcentajeEsperadoPorEtapa || []).filter((p) => p.porcentaje !== null && p.porcentaje !== undefined).map((p) => p.etapaId)));
  const peso = (e) => (e.esFinal ? 0 : e.esDefectuoso ? 2 : 1);
  return (grupo.etapas || [])
    .filter((e) => !e.esInicial && (conObjetivo.has(e._id) || e.tipoObjetivo))
    .sort((a, b) => peso(a) - peso(b) || (a.orden || 0) - (b.orden || 0))
    .map((etapa) => ({ etapa, etiqueta: etiquetaCategoria(etapa), tipo: tipoObjetivoDe(etapa) }));
};

export const categoriasDelReporte = (grupos) => {
  const vistas = new Map();
  grupos.forEach((g) => categoriasDeGrupo(g).forEach((c) => !vistas.has(c.etiqueta) && vistas.set(c.etiqueta, c)));
  const peso = (c) => (c.etiqueta === "OK" ? 0 : c.etiqueta === "SCRAP" ? 2 : 1);
  return [...vistas.values()].sort((a, b) => peso(a) - peso(b)).map((c, i) => ({ etiqueta: c.etiqueta, final: esEtapaFinal(c.etapa), color: COLORES_BIWO[i % COLORES_BIWO.length] }));
};

const esEtapaFinal = (etapa) => etapa.esFinal || etapa.esDefectuoso || etapa.esRetrabajo;

const evaluar = (categorias, base) => {
  categorias.forEach((c) => {
    c.real = base > 0 ? r1((c.llegaron / base) * 100) : null;
    c.cumple = c.objetivo === null || c.real === null ? null : c.tipo === "minimo" ? c.real >= c.objetivo : c.real <= c.objetivo;
  });
  const evaluadas = categorias.filter((c) => c.cumple !== null);
  return evaluadas.length ? evaluadas.every((c) => c.cumple) : null;
};

const datosGenerales = (orden, grupo) => ({
  linea: grupo.nombre,
  cliente: orden.clienteNombre || "",
  folio: orden.folio,
  noParte: [...new Set((orden.matricesResumen || []).map((m) => m.clave).filter(Boolean))].join(", "),
  descripcion: (orden.matricesResumen || []).map((m) => m.descripcion).filter(Boolean).join(", ") || orden.partidaSnapshot?.nombre || "",
  planeadas: orden.cantidadEsperada || 0,
});

const categoriasConPiezas = (orden, grupo, piezasDe) =>
  categoriasDeGrupo(grupo).map(({ etapa, etiqueta, tipo }) => {
    const objetivo = (orden.porcentajeEsperadoPorEtapa || []).find((p) => p.etapaId === etapa._id)?.porcentaje;
    return { etiqueta, tipo, final: esEtapaFinal(etapa), llegaron: piezasDe(etapa._id), objetivo: objetivo === undefined ? null : objetivo };
  });

export const resumenOrden = (orden, grupo) => {
  const categorias = categoriasConPiezas(orden, grupo, (etapaId) => (orden.avance?.etapas || []).find((d) => d.etapaId === etapaId)?.piezas || 0);
  const produccionReal = categorias.reduce((acc, c) => acc + c.llegaron, 0);
  const generales = datosGenerales(orden, grupo);
  return {
    ...generales,
    produccionReal,
    dif: Math.max(0, generales.planeadas - produccionReal),
    categorias,
    cumple: evaluar(categorias, produccionReal),
  };
};

export const resumenPrimeraPasada = (orden, grupo) => {
  const categorias = categoriasConPiezas(orden, grupo, (etapaId) => (orden.primeraPasadaPorEtapa || []).find((r) => r.etapaId === etapaId)?.piezas || 0);
  const clasificadas = categorias.reduce((acc, c) => acc + c.llegaron, 0);
  const generales = datosGenerales(orden, grupo);
  return {
    ...generales,
    clasificadas,
    pendientes: Math.max(0, generales.planeadas - clasificadas),
    cerrada: generales.planeadas > 0 && clasificadas >= generales.planeadas,
    categorias,
    cumple: evaluar(categorias, clasificadas),
  };
};

export const ordenesDelReporte = (grupos) =>
  grupos.flatMap((grupo) => [...grupo.ordenes].sort((a, b) => (a.folio || "").localeCompare(b.folio || "", undefined, { numeric: true })).map((orden) => ({ orden, grupo })));

export const filasDelReporte = (grupos) =>
  grupos.flatMap((grupo) =>
    [...grupo.ordenes].sort((a, b) => (a.clienteNombre || "").localeCompare(b.clienteNombre || "")).map((orden) => resumenOrden(orden, grupo))
  );

export const nombreHojaConFecha = (sufijo) => `${moment().format("DD MM YYYY")} ${sufijo}`.slice(0, 31);

const borde = { style: "thin", color: { rgb: "000000" } };
const bordes = { top: borde, bottom: borde, left: borde, right: borde };
const fuente = (extra = {}) => ({ name: "Calibri", sz: 10, ...extra });
const estilo = ({ fondo, negrita, rotar, alinear = "center", formato, color } = {}) => ({
  font: fuente({ bold: !!negrita, ...(color ? { color: { rgb: color } } : {}) }),
  alignment: { horizontal: alinear, vertical: "center", wrapText: true, ...(rotar ? { textRotation: 90 } : {}) },
  border: bordes,
  ...(fondo ? { fill: { fgColor: { rgb: fondo } } } : {}),
  ...(formato ? { numFmt: formato } : {}),
});

const AZUL = "C5E3F6";
const VERDE_GRUPO = "92D050";
const ROSA_GRUPO = "DA70D6";
const celda = (v, s) => ({ v: v === null || v === undefined ? "" : v, t: typeof v === "number" ? "n" : "s", s });

const hojaReporte = ({ grupos, empresaNombre, etiquetaRango, usuarioNombre }) => {
  const categorias = categoriasDelReporte(grupos);
  const filas = filasDelReporte(grupos);
  const n = categorias.length;
  const fijas = ["LÍNEA", "CLIENTE", "NO. PARTE", "DESCRIPCIÓN", "ORDEN", "PZS PLANEADAS"];
  const iMov = fijas.length + 1;
  const iRes = iMov + 1;
  const iDif = iRes + n * 2;
  const iObj = iDif + 1;
  const iCump = iObj + n;
  const totalCols = iCump + 1;

  const sinBorde = { font: fuente(), alignment: { vertical: "center" } };
  const fila = () => Array.from({ length: totalCols }, () => ({ v: "", t: "s", s: sinBorde }));

  const titulo = fila();
  titulo[0] = celda(empresaNombre || "", { font: fuente({ bold: true, sz: 18 }) });
  const subtitulo = fila();
  subtitulo[0] = celda(`Reporte de producción — ${etiquetaRango || ""}`, { font: fuente({ bold: true, sz: 13, color: { rgb: "006B7A" } }) });
  const generado = fila();
  generado[0] = celda(`Imprimió: ${usuarioNombre || "—"} · ${moment().format("DD/MM/YYYY HH:mm")}`, { font: fuente({ sz: 10, color: { rgb: "595959" } }) });

  const filaGrupo = fila();
  const filaCategoria = fila();
  const filaSub = fila();
  const estiloEnc = estilo({ fondo: AZUL, negrita: true, rotar: true });
  const estiloEncPlano = estilo({ fondo: AZUL, negrita: true });

  [...fijas.map((t, i) => [i, t]), [iMov, "PRODUCCIÓN REAL"], [iDif, "DIF."], [iCump, "CUMP. OBJ."]].forEach(([i, t]) => {
    filaCategoria[i] = celda(t, estiloEnc);
    filaSub[i] = celda("", estiloEnc);
  });
  categorias.forEach((c, k) => {
    filaGrupo[iRes + k * 2] = celda(k === 0 ? "RESULTADOS" : "", estilo({ fondo: VERDE_GRUPO, negrita: true }));
    filaGrupo[iRes + k * 2 + 1] = celda("", estilo({ fondo: VERDE_GRUPO }));
    filaCategoria[iRes + k * 2] = celda(c.etiqueta, estiloEncPlano);
    filaCategoria[iRes + k * 2 + 1] = celda("", estiloEncPlano);
    filaSub[iRes + k * 2] = celda("%", estiloEncPlano);
    filaSub[iRes + k * 2 + 1] = celda("PZS", estiloEncPlano);
    filaGrupo[iObj + k] = celda(k === 0 ? "OBJETIVOS" : "", estilo({ fondo: ROSA_GRUPO, negrita: true }));
    filaCategoria[iObj + k] = celda(c.etiqueta, estiloEncPlano);
    filaSub[iObj + k] = celda("%", estiloEncPlano);
  });

  const datos = filas.map((f) => {
    const r = fila();
    r[0] = celda(f.linea, estilo({ negrita: true }));
    r[1] = celda(f.cliente || "—", estilo({ negrita: true }));
    r[2] = celda(f.noParte, estilo({ alinear: "left" }));
    r[3] = celda(f.descripcion, estilo({ alinear: "left" }));
    r[4] = celda(f.folio, estilo());
    r[5] = celda(f.planeadas, estilo({ formato: "#,##0" }));
    r[iMov] = celda(f.produccionReal, estilo({ formato: "#,##0" }));
    categorias.forEach((c, k) => {
      const dato = f.categorias.find((x) => x.etiqueta === c.etiqueta);
      const color = dato?.cumple === false ? "C00000" : dato?.cumple ? "1E7B1E" : undefined;
      const fondo = dato?.cumple === false ? "FFC7CE" : dato?.cumple ? "C6EFCE" : undefined;
      r[iRes + k * 2] = dato && dato.real !== null ? celda(dato.real / 100, estilo({ formato: "0.0%", color, fondo, negrita: true })) : celda("—", estilo());
      r[iRes + k * 2 + 1] = dato ? celda(dato.llegaron, estilo({ formato: "#,##0", fondo })) : celda("—", estilo());
      r[iObj + k] = dato && dato.objetivo !== null ? celda(dato.objetivo / 100, estilo({ formato: "0.0%" })) : celda("—", estilo());
    });
    r[iDif] = celda(f.dif, estilo({ formato: "#,##0", fondo: "F8CBAD" }));
    r[iCump] = celda(f.cumple === null ? "SIN DATOS" : f.cumple ? "CUMPLE" : "NO CUMPLE", estilo({ negrita: true, fondo: f.cumple === null ? "F2F2F2" : f.cumple ? VERDE_GRUPO : "FF7C80" }));
    return r;
  });

  const FILA_GRUPO = 4;
  const inicioDatos = FILA_GRUPO + 3;
  const vacio = fila();
  const hoja = XLSXStyle.utils.aoa_to_sheet([titulo, subtitulo, generado, vacio, filaGrupo, filaCategoria, filaSub, ...datos]);

  const merges = [];
  const rango = (r1_, c1, r2, c2) => merges.push({ s: { r: r1_, c: c1 }, e: { r: r2, c: c2 } });
  [...fijas.map((_, i) => i), iMov, iDif, iCump].forEach((c) => rango(FILA_GRUPO + 1, c, FILA_GRUPO + 2, c));
  if (n) {
    rango(FILA_GRUPO, iRes, FILA_GRUPO, iRes + n * 2 - 1);
    if (n > 1) rango(FILA_GRUPO, iObj, FILA_GRUPO, iObj + n - 1);
    categorias.forEach((_, k) => rango(FILA_GRUPO + 1, iRes + k * 2, FILA_GRUPO + 1, iRes + k * 2 + 1));
  }
  const combinar = (col, clave) => {
    let inicio = 0;
    for (let i = 1; i <= filas.length; i++) {
      if (i === filas.length || clave(filas[i]) !== clave(filas[inicio])) {
        if (i - inicio > 1) rango(inicioDatos + inicio, col, inicioDatos + i - 1, col);
        inicio = i;
      }
    }
  };
  combinar(0, (f) => f.linea);
  combinar(1, (f) => `${f.linea}|${f.cliente}`);
  hoja["!merges"] = merges;

  hoja["!cols"] = [12, 16, 16, 34, 9, 9, 2, 9, ...categorias.flatMap(() => [7, 7]), 8, ...categorias.map(() => 7), 12].map((wch) => ({ wch }));
  hoja["!rows"] = [{ hpt: 26 }, { hpt: 18 }, {}, {}, { hpt: 16 }, { hpt: 60 }, { hpt: 16 }];
  return hoja;
};

const hojaPrimeraPasada = ({ orden, grupo, empresaNombre, usuarioNombre }) => {
  const r = resumenPrimeraPasada(orden, grupo);
  const titulo = { font: fuente({ bold: true, sz: 18 }) };
  const sub = { font: fuente({ bold: true, sz: 13, color: { rgb: "006B7A" } }) };
  const meta = { font: fuente({ sz: 10, color: { rgb: "595959" } }) };
  const etiqueta = estilo({ fondo: AZUL, negrita: true, alinear: "left" });
  const valor = estilo({ alinear: "left" });
  const info = [
    ["LÍNEA", r.linea],
    ["CLIENTE", r.cliente || "—"],
    ["ORDEN", r.folio],
    ["NO. PARTE", r.noParte],
    ["DESCRIPCIÓN", r.descripcion],
    ["PZS PLANEADAS", r.planeadas],
    ["PZS EN 1A PASADA", r.clasificadas],
    ["PZS SIN CLASIFICAR", r.pendientes],
  ];
  const estadoCelda = celda(r.cerrada ? "CERRADA" : "EN CURSO", estilo({ negrita: true, fondo: r.cerrada ? VERDE_GRUPO : "FFE699" }));
  const encabezado = ["CATEGORÍA", "PZS", "% REAL", "% OBJETIVO", "TIPO DE OBJETIVO", "CUMPLE"].map((t) => celda(t, estilo({ fondo: AZUL, negrita: true })));
  const filasCategoria = r.categorias.map((c) => {
    const fondo = c.cumple === false ? "FFC7CE" : c.cumple ? "C6EFCE" : undefined;
    return [
      celda(c.etiqueta, estilo({ negrita: true, alinear: "left" })),
      celda(c.llegaron, estilo({ formato: "#,##0", fondo })),
      c.real === null ? celda("—", estilo()) : celda(c.real / 100, estilo({ formato: "0.0%", negrita: true, fondo, color: c.cumple === false ? "C00000" : c.cumple ? "1E7B1E" : undefined })),
      c.objetivo === null ? celda("—", estilo()) : celda(c.objetivo / 100, estilo({ formato: "0.0%" })),
      celda(c.objetivo === null ? "—" : c.tipo === "minimo" ? "Mínimo" : "Máximo", estilo()),
      celda(c.cumple === null ? "—" : c.cumple ? "SÍ" : "NO", estilo({ negrita: true, fondo })),
    ];
  });
  const total = [
    celda("TOTAL", estilo({ negrita: true, fondo: AZUL, alinear: "left" })),
    celda(r.clasificadas, estilo({ formato: "#,##0", negrita: true, fondo: AZUL })),
    celda(r.clasificadas ? 1 : "—", estilo({ formato: "0.0%", negrita: true, fondo: AZUL })),
    celda("", estilo({ fondo: AZUL })),
    celda("CUMP. OBJ.", estilo({ negrita: true, fondo: AZUL })),
    celda(r.cumple === null ? "SIN DATOS" : r.cumple ? "CUMPLE" : "NO CUMPLE", estilo({ negrita: true, fondo: r.cumple === null ? "F2F2F2" : r.cumple ? VERDE_GRUPO : "FF7C80" })),
  ];
  const vacia = [];
  const aoa = [
    [celda(empresaNombre || "", titulo)],
    [celda(`First Time Quality — ${r.folio}`, sub)],
    [celda(`Imprimió: ${usuarioNombre || "—"} · ${moment().format("DD/MM/YYYY HH:mm")}`, meta)],
    [celda("Cada pieza original cuenta una sola vez, en el primer resultado al que llegó.", meta)],
    vacia,
    ...info.map(([e, v]) => {
      const s = typeof v === "number" ? estilo({ alinear: "left", formato: "#,##0" }) : valor;
      return [celda(e, etiqueta), celda(v, s), ...Array.from({ length: 4 }, () => celda("", s))];
    }),
    [celda("ESTADO", etiqueta), estadoCelda, ...Array.from({ length: 4 }, () => celda("", estadoCelda.s))],
    vacia,
    encabezado,
    ...filasCategoria,
    total,
  ];
  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = [22, 16, 12, 12, 18, 14].map((wch) => ({ wch }));
  hoja["!merges"] = info.map((_, i) => ({ s: { r: 5 + i, c: 1 }, e: { r: 5 + i, c: 5 } })).concat([{ s: { r: 5 + info.length, c: 1 }, e: { r: 5 + info.length, c: 5 } }]);
  hoja["!rows"] = [{ hpt: 26 }, { hpt: 18 }];
  return hoja;
};

export const descargarReporteDiaProduccion = ({ grupos, empresaNombre, etiquetaRango, usuarioNombre }) => {
  descargarLibro(
    [
      { nombre: nombreHojaConFecha("Producción"), hoja: hojaReporte({ grupos, empresaNombre, etiquetaRango, usuarioNombre }) },
      ...ordenesDelReporte(grupos).map(({ orden, grupo }) => ({
        nombre: nombreHojaConFecha(`FTQ ${orden.folio}`),
        hoja: hojaPrimeraPasada({ orden, grupo, empresaNombre, usuarioNombre }),
      })),
    ],
    `${moment().format("DD MM YYYY")} Reporte_produccion_${limpiarNombreArchivo(etiquetaRango)}`
  );
};
